import type { ScanDataMutators } from '@/Client/State';
import {
  getScanGroup,
  getScanTask,
  getScanWithUpdatedGroup,
  getScopeForScanPartChildren,
  initTaskFromTemplate,
  type ScanTaskOptimized,
} from '@kaiizen/kai-scan-template-system';

/**
 * Replaces whatever task currently sits at `taskId` with a freshly initialized instance from its
 * own template, then applies `overrides` on top. Shared by removeTaskFromScope and
 * addFreshTaskToScope below — they differ only in whether the fresh instance keeps the original
 * id and which scope flag it lands with.
 *
 * Returns the id of the task now in that slot, so a caller that needs to navigate to it (the id
 * may have changed) doesn't have to re-derive it.
 */
const replaceTaskWithFreshInstance = async (
  scanDataMutators: ScanDataMutators,
  groupId: string,
  taskId: string,
  overrides: Partial<ScanTaskOptimized>,
  { keepId }: { keepId: boolean },
): Promise<string> => {
  let resultTaskId = taskId;
  await scanDataMutators.setScan(currentScan => {
    const group = getScanGroup(currentScan, groupId);
    const task = getScanTask(currentScan, groupId, taskId, group);
    const scope = getScopeForScanPartChildren(group);
    const freshTask: ScanTaskOptimized = {
      ...initTaskFromTemplate(task.template, scope),
      // Photos document real-world state, not scope selections - like id,
      // they carry forward only when the slot might come back (keepId), not
      // into a genuinely new task.
      ...(keepId ? { id: task.id, photos: task.photos } : {}),
      ...overrides,
    };
    resultTaskId = freshTask.id;
    return getScanWithUpdatedGroup(currentScan, groupId, {
      tasks: group.tasks?.map(candidateTask => candidateTask.id === taskId ? freshTask : candidateTask),
    });
  });
  return resultTaskId;
};

/**
 * Removes a task from the current scope by reinitializing it from its own template — the same
 * fresh state a brand-new task gets — rather than deleting it outright, then marking it
 * `notApplicable`. `isScopeNavigatorTask` reads that field, so the task drops out of the visible
 * scope immediately.
 *
 * Reinitializing (instead of only zeroing costs) means every prior selection — contractor
 * assignment, options, line items, custom values — is actually discarded, not just hidden. `id`
 * is kept while the task sits removed so anything already keyed to it (e.g. notes) doesn't orphan
 * mid-flight, and photos carry forward too (they document real-world state, not a scope
 * selection) — but note that `addFreshTaskToScope` mints a new id when the slot is reused, so
 * that continuity ends the moment the task is added back.
 *
 * The task instance is left in place in `group.tasks` rather than removed, so the group's "+" add-task
 * search (`getAddableTasks` in KaiNavigator.tsx) can still offer it back.
 */
export const removeTaskFromScope = async (
  scanDataMutators: ScanDataMutators,
  groupId: string,
  taskId: string,
): Promise<void> => {
  await replaceTaskWithFreshInstance(scanDataMutators, groupId, taskId, { notApplicable: true }, { keepId: true });
};

/**
 * Adds a task from the group's "+" add-task search back into scope — but as a genuinely new
 * instance, not a revival of whatever used to sit in that slot. A new id, template-fresh values,
 * `userAdded: true`. Whatever was there before (including a task removed by removeTaskFromScope)
 * is replaced outright, not restored.
 *
 * Returns the new task's id, since the caller navigates to it and the id is not the one it was
 * called with.
 */
export const addFreshTaskToScope = (
  scanDataMutators: ScanDataMutators,
  groupId: string,
  taskId: string,
): Promise<string> => replaceTaskWithFreshInstance(scanDataMutators, groupId, taskId, { userAdded: true }, { keepId: false });
